import { DeferredTooltipProvider } from "@/components/DeferredTooltipProvider";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, useRoutes } from "react-router-dom";
import { lazy, Suspense, useEffect, useState } from "react";

import { routes } from "@/routes";
import { BackToTop } from "@/components/BackToTop";
import ScrollToTop from "@/components/ScrollToTop";
import { RouteSEO } from "@/components/RouteSEO";
import { SchemaValidator } from "@/components/SchemaValidator";
import { FloatingAssessmentCTA } from "@/components/FloatingAssessmentCTA";
import { ScrollProgress } from "@/components/ScrollProgress";

// The Strategy Assistant bubble only matters after the user scrolls, so we
// defer the entire assistant (and its framer-motion + knowledge-base weight)
// out of the initial bundle. It mounts on idle after first paint.
const StrategyAssistantBubble = lazy(() =>
  import("@/components/StrategyAssistantBubble").then((m) => ({
    default: m.StrategyAssistantBubble,
  })),
);

// Toaster / Sonner are notification overlays — they don't wrap the route tree,
// so we can lazy-load them and keep sonner + @radix-ui/react-toast out of the
// initial homepage bundle (the largest remaining mobile-JS offender).
const LazyToaster = lazy(() =>
  import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })),
);
const LazySonner = lazy(() =>
  import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })),
);

// react-query is only used by a handful of lazy-loaded pages. We keep a single
// stable client instance. The provider wraps the route tree eagerly so it does
// not remount the routes on mount; the library itself is ~8 KB gzipped.
const queryClient = new QueryClient();

import { AuthProvider } from "@/contexts/AuthContext";
import { ProfileProvider } from "@/contexts/ProfileContext";
import { ThemeProvider } from "@/components/ThemeProvider";

/**
 * Notification overlays mount after the first paint so their dependencies
 * (sonner + Radix toast) stay out of the critical-path bundle. They are
 * standalone portals, not wrappers, so deferring them never remounts routes.
 */
const NotificationOverlays = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const id = window.requestIdleCallback
      ? window.requestIdleCallback(() => setShow(true), { timeout: 1200 })
      : window.setTimeout(() => setShow(true), 0);
    return () => {
      if (typeof id === "number") window.clearTimeout(id);
      else window.cancelIdleCallback(id as number);
    };
  }, []);
  if (!show) return null;
  return (
    <Suspense fallback={null}>
      <LazyToaster />
      <LazySonner />
    </Suspense>
  );
};

/** Renders the route table via useRoutes so App stays declarative. */
const AppRoutes = () => useRoutes(routes);

const App = () => (
  <ThemeProvider
    attribute="class"
    defaultTheme="light"
    enableSystem={false}
    disableTransitionOnChange
  >
    <AuthProvider>
      <ProfileProvider>
        <QueryClientProvider client={queryClient}>
          <DeferredTooltipProvider>
            <BrowserRouter>
              <ScrollToTop />
              <RouteSEO />
              <SchemaValidator />
              <ScrollProgress />
              <BackToTop />
              <FloatingAssessmentCTA />
              <Suspense fallback={null}>
                <StrategyAssistantBubble />
              </Suspense>
              <AppRoutes />
            </BrowserRouter>
            <NotificationOverlays />
          </DeferredTooltipProvider>
        </QueryClientProvider>
      </ProfileProvider>
    </AuthProvider>
  </ThemeProvider>
);

export default App;
